import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CanonicalSkillAutoCompleteResultItem, JobseekerSkillItem, JobseekerSkillViewModel, SkillAutoCompleteResult, SkillBaseTypeEnum } from '@hrra/common-models';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/root-store/state';
import {
  addCustomJobseekerSkillForAdmin,
  addJobseekerSkillForAdmin,
  loadSkillSuggestionsForAdmin,
  removeJobseekerSkillForAdmin,
  reorderJobseekerSkillsForAdmin,
  saveJobseekerSkillsForAdmin,
} from 'src/app/root-store/jobseeker-store/jobseeeker-profile-for-admin/actions';
import {
  selectDraftJobseekerSkills,
  selectHasExactSkillMatch,
  selectJobseekerModerationLastChangeDate,
  selectJobseekerSkills,
  selectSkillSuggestions,
} from 'src/app/root-store/jobseeker-store/jobseeeker-profile-for-admin/selectors';

//dialog shell: binds the admin jobseeker profile store to the shared skills edit form
@Component({
    selector: 'app-jobseeker-skills-edit-for-admin',
    templateUrl: './redesign/jobseeker-skills-edit-for-admin.component.html',
    standalone: false
})
export class JobseekerSkillsEditForAdminComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  public queryChange$ = new Subject<string | undefined>()

  draftSkills$: Observable<JobseekerSkillViewModel[]>;
  savedSkills$: Observable<JobseekerSkillItem[]>;
  suggestions$: Observable<SkillAutoCompleteResult>;
  hasExactMatch$: Observable<boolean>;
  moderationLastChangeDate$: Observable<Date>;

  public title: string;
  public skillBaseType: SkillBaseTypeEnum;
  public placeholder: string;
  public jobseekerId: number;

  constructor(
    private store: Store<AppState>,
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<JobseekerSkillsEditForAdminComponent>
  ) {}

  ngOnInit(): void {
    this.title = this.data.title;
    this.skillBaseType = this.data.skillBaseType;
    this.placeholder = this.skillBaseType === SkillBaseTypeEnum.Soft ? 'label.SoftSkillsExample' : 'label.SkillsExample';
    this.jobseekerId = this.data.jobseekerId;

    this.draftSkills$ = this.store.pipe(select(selectDraftJobseekerSkills));
    this.savedSkills$ = this.store.pipe(select(selectJobseekerSkills(this.skillBaseType)));
    this.suggestions$ = this.store.pipe(select(selectSkillSuggestions));
    this.hasExactMatch$ = this.store.pipe(select(selectHasExactSkillMatch));

    this.moderationLastChangeDate$ = this.store.pipe(select(selectJobseekerModerationLastChangeDate));

    combineLatest([this.draftSkills$, this.queryChange$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([draftSkills, query]) => {
        this.store.dispatch(
          loadSkillSuggestionsForAdmin({
            payload: {
              query: query ?? '',
              skillBaseType: this.skillBaseType,
              selectedCanonicalSkillIds: (draftSkills ?? [])
                .filter((s) => !s.isCustom)
                .map((s) => s.canonicalSkillId),
              selectedCustomSkills: (draftSkills ?? [])
                .filter((s) => s.isCustom)
                .map((s) => s.name),
            },
          })
        );
      });
  }

  onQueryChange(query: string) {
    this.queryChange$.next(query)
  }

  onSelectSkill(skill: CanonicalSkillAutoCompleteResultItem) {
    this.store.dispatch(addJobseekerSkillForAdmin({ payload: { skill: skill } }));
  }

  onAddCustomSkill(name: string) {
    this.store.dispatch(addCustomJobseekerSkillForAdmin({ payload: { name: name } }));
  }

  onRemoveSkill(index: number) {
    this.store.dispatch(removeJobseekerSkillForAdmin({ payload: { index: index } }));
  }

  onReorderSkills(event: { previousIndex: number; currentIndex: number }) {
    this.store.dispatch(reorderJobseekerSkillsForAdmin({ payload: event }));
  }

  onSave(moderationLastChangeDate: Date, skills: JobseekerSkillViewModel[]) {
    this.store.dispatch(
      saveJobseekerSkillsForAdmin({
        payload: {
          skills: skills,
          skillBaseType: this.skillBaseType,
          jobseekerId: this.jobseekerId,
          moderationLastChangeDate: moderationLastChangeDate,
        },
      })
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
